import { Box, Group, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatches } from 'react-router-dom';

import { IconFigmaBell } from '@/assets/icons';
import { useBreadcrumbExtra } from '@/components/layout/BreadcrumbContext';
import { SHOW_NOTIFICATION_DOT } from '@/config/featureFlags';
import { useAuthContext } from '@/features/auth/context/AuthContext';

interface RouteHandle {
  breadcrumbKey?: string | string[];
}

export const PageHeader = () => {
  const { t } = useTranslation();
  const matches = useMatches();
  const location = useLocation();
  const { user } = useAuthContext();
  const extraCrumbs = useBreadcrumbExtra();
  const isAdmin = location.pathname.startsWith('/admin');

  const crumbs = matches
    .flatMap((match) => {
      const key = (match.handle as RouteHandle | undefined)?.breadcrumbKey;
      return key ? (Array.isArray(key) ? key : [key]) : [];
    })
    .map((key) => t(key));

  crumbs.push(...extraCrumbs);

  const crumbsRow = (
    <Group gap={8} wrap="nowrap">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          <Group key={crumb} gap={8} wrap="nowrap">
            {index > 0 && (
              <Text fz={12.5} style={{ color: 'var(--color-text-muted)', opacity: 0.5 }}>
                /
              </Text>
            )}
            <Text
              fz={12.5}
              fw={isLast ? 600 : 400}
              style={{ color: isLast ? 'var(--color-text-primary)' : 'var(--color-text-muted)' }}
            >
              {crumb}
            </Text>
          </Group>
        );
      })}
    </Group>
  );

  const orgTag = (isAdmin || user) && (
    <Box
      px={11}
      py={5}
      style={{
        borderRadius: 999,
        backgroundColor: 'var(--color-bg-surface-2)',
        width: 'fit-content'
      }}
    >
      <Text fz={11.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
        {isAdmin ? t('adminNav.tagline') : user?.orgName}
      </Text>
    </Box>
  );

  // Samo desktop — na mobitelu ime firme ide u AppHeader, a breadcrumbs se ne prikazuju
  // (stranice imaju vlastiti naslov, a traka bi uzimala okomiti prostor).
  return (
    <Box
      px={32}
      bg="var(--color-bg-surface)"
      bd="1px solid var(--color-border-soft)"
      style={{ borderInline: 'none', borderTop: 'none' }}
      visibleFrom="sm"
    >
      <Group h={56} justify="space-between" wrap="nowrap">
        {crumbsRow}
        <Group gap={16} wrap="nowrap">
          <Box style={{ display: 'flex', color: 'var(--color-text-muted)' }}>
            <IconFigmaBell size={17} withDot={SHOW_NOTIFICATION_DOT} />
          </Box>
          {orgTag}
        </Group>
      </Group>
    </Box>
  );
};
